import { useMemo } from 'react'
import { Scheduling, BlocksData } from './useScheduling'

export type TimelineItem =
  | { type: 'appointment'; data: Scheduling }
  | { type: 'block'; data: BlocksData }

const extractTime = (value?: string | null) => {
  if (!value) return '23:59'

  if (value.includes('T')) {
    return value.substring(11, 16)
  }

  return value.substring(0, 5)
}

const toMinutes = (time: string) => {
  const [hour, minute] = time.split(':').map(Number)

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return 23 * 60 + 59
  }

  return hour * 60 + minute
}

// JS: 0=domingo ... 6=sábado
// API do bloqueio: 1=segunda ... 6=sábado, 7=domingo
const getApiWeekDay = (date: Date) => {
  const jsDay = date.getDay()
  return jsDay === 0 ? '7' : String(jsDay)
}

const isSameDate = (dateA: Date, dateB: Date) => {
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  )
}

const isBlockVisibleOnSelectedDate = (
  block: BlocksData,
  selectedDate: string
) => {
  if (!selectedDate) return true

  const targetDate = new Date(`${selectedDate}T00:00:00`)

  if (block.is_recurrent) {
    const selectedWeekDay = getApiWeekDay(targetDate)
    const allowedDays = (block.week_day || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)

    return allowedDays.includes(selectedWeekDay)
  }

  if (block.start_time) {
    const startDate = new Date(block.start_time)
    return isSameDate(startDate, targetDate)
  }

  if (block.finish_time) {
    const finishDate = new Date(block.finish_time)
    return isSameDate(finishDate, targetDate)
  }

  return false
}

export function useSchedulingTimeline(
  scheduling: Scheduling[] = [],
  blocks: BlocksData[] = [],
  selectedDate: string
): TimelineItem[] {
  return useMemo(() => {
    const filteredBlocks = blocks.filter((block) =>
      isBlockVisibleOnSelectedDate(block, selectedDate)
    )

    const merged: TimelineItem[] = [
      ...scheduling.map((item) => ({
        type: 'appointment' as const,
        data: item
      })),
      ...filteredBlocks.map((item) => ({
        type: 'block' as const,
        data: item
      }))
    ]

    merged.sort((a, b) => {
      const timeA =
        a.type === 'appointment'
          ? extractTime(a.data.scheduled_initial_time)
          : extractTime(a.data.start_time || a.data.recurrent_start_time)

      const timeB =
        b.type === 'appointment'
          ? extractTime(b.data.scheduled_initial_time)
          : extractTime(b.data.start_time || b.data.recurrent_start_time)

      return toMinutes(timeA) - toMinutes(timeB)
    })

    return merged
  }, [scheduling, blocks, selectedDate])
}
